import React, { useRef, useState } from "react";
import { Button } from "primereact/button";
import { Menu } from "primereact/menu";
import { Dialog } from "primereact/dialog";

const getPriceFormValues = (priceAmount, priceCurrency) => {
  return {
    priceValue: priceAmount != null ? String(priceAmount) : "",
    priceCurrency: ["CHF", "€"].includes(priceCurrency) ? priceCurrency : "€"
  };
};

const RowActions = ({ user, canUpdatePrice = false, onUpdatePrice = async () => {} }) => {
  const pilgrimName = [user?.Name, user?.Surname].filter(Boolean).join(" ").trim() || "Unknown pilgrim";
  const menuRef = useRef(null);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [dialogContent, setDialogContent] = useState(null);
  const [dialogHeader, setDialogHeader] = useState("Info");
  const [priceDialogVisible, setPriceDialogVisible] = useState(false);
  const [priceValue, setPriceValue] = useState("");
  const [priceCurrency, setPriceCurrency] = useState("€");
  const [priceError, setPriceError] = useState("");
  const [isSubmittingPrice, setIsSubmittingPrice] = useState(false);

  const showDialog = (header, content) => {
    setDialogHeader(header);
    setDialogContent(content);
    setDialogVisible(true);
  };

  const showRegistrantData = () => {
    const registrantFields = [
      { label: "Name", value: user?.RegistrantName },
      { label: "Surname", value: user?.RegistrantSurname },
      { label: "Email", value: user?.RegistrantEmail },
      { label: "Phone", value: user?.RegistrantPhone }
    ];
    const hasRegistrantData = registrantFields.some(({ value }) => (value || "").trim().length > 0);

    showDialog(
      "Registrant data",
      hasRegistrantData ? (
        <div className="flex flex-column gap-3">
          {registrantFields.map(({ label, value }) => (
            <div key={label}>
              <div className="text-sm text-500">{label}</div>
              <div>{value || "Not available"}</div>
            </div>
          ))}
        </div>
      ) : (
        <p className="m-0">No registrant data available for this pilgrim.</p>
      )
    );
  };

  const resendRegistrationEmail = async () => {
    if (!user?.FamilyID) {
      showDialog("Error", <p className="m-0">No family ID available for this user.</p>);
      return;
    }
    try {
      const response = await fetch(`./backend/email/payload.php?familyID=${encodeURIComponent(user.FamilyID)}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || result.status === 'error') {
        throw new Error(result.message || `HTTP error! status: ${response.status}`);
      }
      showDialog("Success", <p className="m-0">{result.message || "Resend requested."}</p>);
    } catch (error) {
      console.error("Resend failed:", error);
      showDialog("Error", <p className="m-0">{error.message || "Failed to resend the email. Please try again."}</p>);
    }
  };

  const openPriceDialog = () => {
    if (!canUpdatePrice) {
      showDialog("Error", <p className="m-0">Your admin account has no write access to this pilgrim&apos;s BusLine.</p>);
      return;
    }

    const formValues = getPriceFormValues(user?.priceAmount, user?.priceCurrency);
    setPriceValue(formValues.priceValue);
    setPriceCurrency(formValues.priceCurrency);
    setPriceError("");
    setPriceDialogVisible(true);
  };

  const handlePriceDialogHide = () => {
    if (isSubmittingPrice) {
      return;
    }
    setPriceDialogVisible(false);
    setPriceError("");
  };

  const submitPriceUpdate = async () => {
    const normalizedPriceValue = priceValue.trim();
    const numericPrice = Number(normalizedPriceValue);

    if (!normalizedPriceValue || !Number.isInteger(numericPrice) || numericPrice <= 0 || numericPrice > 999) {
      setPriceError("Price must be a whole number between 1 and 999.");
      return;
    }

    if (!["CHF", "€"].includes(priceCurrency)) {
      setPriceError("Currency must be CHF or €.");
      return;
    }

    setIsSubmittingPrice(true);
    setPriceError("");
    try {
      await onUpdatePrice(user, numericPrice, priceCurrency);
      setPriceDialogVisible(false);
    } catch (error) {
      console.error("Price update failed:", error);
      setPriceError(error.message || "Failed to update the price. Please try again.");
    } finally {
      setIsSubmittingPrice(false);
    }
  };

  const menuModel = [
    {
      label: "View registrant data",
      icon: "pi pi-user",
      command: showRegistrantData
    },
    {
      label: "Resend registration email",
      icon: "pi pi-envelope",
      command: resendRegistrationEmail
    },
    {
      label: "Update price",
      icon: "pi pi-wallet",
      command: openPriceDialog
    }
  ];

  return (
    <>
      <Dialog
        header={dialogHeader}
        visible={dialogVisible}
        onHide={() => setDialogVisible(false)}
        style={{ width: "24rem" }}
        draggable={false}
      >
        {dialogContent}
      </Dialog>
      <Dialog
        header="Update price"
        visible={priceDialogVisible}
        onHide={handlePriceDialogHide}
        style={{ width: "28rem" }}
        draggable={false}
        footer={(
          <div className="flex justify-content-end gap-2">
            <Button
              label="Cancel"
              className="p-button-text"
              onClick={handlePriceDialogHide}
              disabled={isSubmittingPrice}
            />
            <Button
              label="Save"
              onClick={submitPriceUpdate}
              loading={isSubmittingPrice}
            />
          </div>
        )}
      >
        <div className="flex flex-column gap-3">
          <div className="flex flex-column gap-1">
            <span className="text-sm text-500">Pilgrim</span>
            <strong>{pilgrimName}</strong>
          </div>
          <div className="flex flex-column gap-2">
            <label htmlFor={`price-value-${user?.UserID}`}>Price</label>
            <input
              id={`price-value-${user?.UserID}`}
              type="number"
              min="1"
              max="999"
              step="1"
              value={priceValue}
              onChange={(e) => setPriceValue(e.target.value)}
              className="p-inputtext p-component w-full"
              disabled={isSubmittingPrice}
            />
          </div>
          <div className="flex flex-column gap-2">
            <span>Currency</span>
            <div className="flex gap-4">
              {["CHF", "€"].map((currency) => (
                <label key={currency} className="flex align-items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name={`price-currency-${user?.UserID}`}
                    value={currency}
                    checked={priceCurrency === currency}
                    onChange={(e) => setPriceCurrency(e.target.value)}
                    disabled={isSubmittingPrice}
                  />
                  <span>{currency}</span>
                </label>
              ))}
            </div>
          </div>
          {priceError ? <small className="p-error">{priceError}</small> : null}
        </div>
      </Dialog>
      <Menu model={menuModel} popup ref={menuRef} />
      <Button
        icon="pi pi-ellipsis-v"
        className="p-button-text p-button-rounded p-button-plain"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          menuRef.current?.toggle(e);
        }}
        aria-haspopup
        aria-controls="user-actions-menu"
      />
    </>
  );
};

export default RowActions;
